import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DocumentTextIcon } from '../../icons';
import MatDashTableCard from '../layout/MatDashTableCard';
import { formatDateTime } from '../../utils/transcriptionMeta';
import { normalizeJobStatus } from '../../utils/transcriptionStatus';

const STATUS_META = {
  pending: { label: 'Pending', className: 'bg-amber-50 text-amber-800 border-amber-200' },
  uploading: { label: 'Processing', className: 'bg-sky-50 text-sky-800 border-sky-200' },
  complete: { label: 'Complete', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  failed: { label: 'Failed', className: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const toSessionRow = (session) => {
  const raw = String(session?.status || session?.state || '').toLowerCase();
  const jobStatus = normalizeJobStatus(session?.status);
  const hasTranscript = Boolean(
    (session?.transcribed_content || session?.content || session?.text || '').trim()
  );
  let statusKey = 'pending';
  if (jobStatus === 'failed' || raw === 'failed') {
    statusKey = 'failed';
  } else if (jobStatus === 'complete' || hasTranscript) {
    statusKey = 'complete';
  } else if (raw === 'uploading' || raw === 'queued' || jobStatus === 'processing' || jobStatus === 'queued') {
    statusKey = 'uploading';
  }

  const rawId = String(session?.id || '').replace(/^demo-/, '');
  const sessionId = session?.session_id || (rawId ? `SES-${rawId.toUpperCase()}` : '—');

  return {
    id: session.id,
    session_id: sessionId,
    case_number: session.case_reference || session.case_number || '—',
    case_title: session.title || session.case_title || 'Untitled session',
    recorded_at: session.recorded_at || formatDateTime(session.created_at) || '—',
    statusKey,
  };
};

const RecentSessionsPanel = ({ sessions = [] }) => {
  const navigate = useNavigate();
  const rows = sessions.map(toSessionRow);

  return (
    <MatDashTableCard
      title="Recent sessions"
      subtitle="Latest proceedings in your court"
      headerAction={
        <Link to="/sessions" className="md-link text-sm">
          View all
        </Link>
      }
    >
      {rows.length > 0 ? (
        <>
          <div className="block sm:hidden divide-y divide-matdash-border">
            {rows.map((row) => {
              const meta = STATUS_META[row.statusKey] || STATUS_META.pending;
              return (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => navigate(`/transcriptions/${row.id}`)}
                  className="block w-full text-left p-4 hover:bg-matdash-primary-subtle/30 transition-colors"
                >
                  <div className="flex justify-between items-start gap-2 mb-1">
                    <p className="text-sm font-semibold text-matdash-dark mb-0">{row.session_id}</p>
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border shrink-0 ${meta.className}`}>
                      {meta.label}
                    </span>
                  </div>
                  <p className="text-sm text-matdash-dark mb-0.5">{row.case_title}</p>
                  <p className="text-xs text-matdash-muted mb-0">
                    {row.case_number} · {row.recorded_at}
                  </p>
                </button>
              );
            })}
          </div>

          <div className="hidden sm:block md-table-wrap">
            <table className="md-table">
              <thead>
                <tr>
                  <th>Session ID</th>
                  <th>Case Number</th>
                  <th>Case Title</th>
                  <th>Recording Date / Time</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const meta = STATUS_META[row.statusKey] || STATUS_META.pending;
                  return (
                    <tr
                      key={row.id}
                      onClick={() => navigate(`/transcriptions/${row.id}`)}
                      className="cursor-pointer hover:bg-matdash-primary-subtle/30"
                    >
                      <td className="font-semibold whitespace-nowrap">{row.session_id}</td>
                      <td className="text-matdash-muted whitespace-nowrap">{row.case_number}</td>
                      <td className="min-w-[220px]">{row.case_title}</td>
                      <td className="text-matdash-muted whitespace-nowrap">{row.recorded_at}</td>
                      <td className="whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${meta.className}`}>
                          {meta.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="md-empty-state">
          <DocumentTextIcon className="h-10 w-10 text-matdash-border mx-auto mb-2" />
          <p className="font-medium text-matdash-dark">No sessions found</p>
          <p className="text-sm mt-1">Start by recording or uploading a proceeding</p>
        </div>
      )}
    </MatDashTableCard>
  );
};

export default RecentSessionsPanel;
